import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  Search,
  Filter,
  Eye
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showError } from '../../utils/notificationHandler';
import PageHeader from '../shared/PageHeader';

const InquiryManagement = ({ context = 'staff' }) => {
  const history = useHistory();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('submitted');
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Fetch inquiries
  useEffect(() => {
    fetchInquiries();
  }, [filterStatus, showArchived]);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('inquiry_lists')
        .select(`
          *,
          accounts:user_id (
            full_name,
            company_name,
            email
          )
        `)
        .eq('is_archived', showArchived)
        .order('updated_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Get item counts for each inquiry
      const inquiriesWithCounts = await Promise.all(
        data.map(async (inquiry) => {
          const { count } = await supabase
            .from('inquiry_items')
            .select('*', { count: 'exact', head: true })
            .eq('inquiry_list_id', inquiry.id);

          return { ...inquiry, item_count: count || 0 };
        })
      );

      setInquiries(inquiriesWithCounts);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      showError('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  // Filter inquiries by search term
  const filteredInquiries = inquiries.filter(inquiry => {
    const customerName = inquiry.accounts?.company_name || inquiry.accounts?.full_name || '';
    const matchesSearch = 
      inquiry.list_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inquiry.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
      submitted: { color: 'bg-blue-100 text-blue-800', label: 'Submitted' },
      under_review: { color: 'bg-yellow-100 text-yellow-800', label: 'Under Review' },
      quoted: { color: 'bg-green-100 text-green-800', label: 'Quoted' },
      converted: { color: 'bg-purple-100 text-purple-800', label: 'Converted' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <PageHeader
        title="Inquiries (RFQ)"
        description="Review and quote customer inquiries (RFQs)"
        className="mb-6"
      />

      {/* Controls */}
      <div className="mb-6 space-y-4">
        {/* Status Filters */}
        <div className="flex flex-wrap gap-2">
          {['all', 'submitted', 'under_review', 'quoted', 'converted'].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search inquiries by name, customer, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Archive Toggle */}
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>Show archived inquiries</span>
          </label>
          {showArchived && (
            <span className="text-xs text-gray-500">
              (Viewing {filteredInquiries.length} archived)
            </span>
          )}
        </div>
      </div>

      {/* Inquiries List */}
      {filteredInquiries.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || filterStatus !== 'all' ? 'No inquiries found' : 'No inquiries yet'}
          </h3>
          <p className="text-gray-600">
            {searchTerm || filterStatus !== 'all'
              ? 'Try adjusting your search or filter'
              : 'Inquiries will appear here when customers submit them'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inquiry
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <AnimatePresence>
                {filteredInquiries.map(inquiry => (
                  <motion.tr
                    key={inquiry.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={`hover:bg-gray-50 transition-colors ${
                      inquiry.is_archived ? 'bg-gray-100 opacity-75' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-gray-900">
                          {inquiry.list_name}
                        </div>
                        {inquiry.is_archived && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                            Archived
                          </span>
                        )}
                      </div>
                      {inquiry.description && (
                        <div className="text-sm text-gray-500 line-clamp-1">
                          {inquiry.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {inquiry.accounts?.company_name || inquiry.accounts?.full_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {inquiry.accounts?.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {inquiry.item_count}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(inquiry.status)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(inquiry.submitted_at)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          const basePath = context === 'admin' ? '/admin' : '/staff-area';
                          history.push(`${basePath}/inquiry/${inquiry.id}`);
                        }}
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-900"
                      >
                        <Eye size={16} />
                        Review
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      )}

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="text-gray-600 text-sm font-medium">Submitted</div>
          <div className="text-2xl font-bold text-gray-900">
            {inquiries.filter(i => i.status === 'submitted').length}
          </div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="text-gray-600 text-sm font-medium">Under Review</div>
          <div className="text-2xl font-bold text-gray-900">
            {inquiries.filter(i => i.status === 'under_review').length}
          </div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="text-gray-600 text-sm font-medium">Quoted</div>
          <div className="text-2xl font-bold text-gray-900">
            {inquiries.filter(i => i.status === 'quoted').length}
          </div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="text-gray-600 text-sm font-medium">Converted</div>
          <div className="text-2xl font-bold text-gray-900">
            {inquiries.filter(i => i.status === 'converted').length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InquiryManagement;
